// PHASE_1 / B0 — the file-leave form Content component (ADR-014 §2 + gap §9.1).
//
// LeaveFormContent is the SINGLE form both the self-service screen (`me-leave`)
// and its admin twin render through the one parameterized AppShell — the
// brief's single-source-of-truth rule (gap §10.1). One implementation, two
// contexts; no `*FormV2` fork.
//
// DE-COUPLING (gap §9.1 — resolved): the İSTON design's dual-ERP vekâlet
// selector (Axapta + EBS delegation, two separate target pickers) COLLAPSES to
// ONE native delegation field — a single "delegate to" employee picker against
// NovemAI's own org graph. No external-ERP delegation concept survives.
//
// Built on the shared <EntityForm> (zod-style validator port — no hard zod
// dependency in the app; a hand-rolled safeParse satisfies the contract). All
// labels are i18n keys (ADR-006). Hours are DECIMALS (`_hours`), never money.

import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Card,
  CardBody,
  PageHeader,
  Button,
  EntityForm,
  FormField,
  Input,
  Select,
  Textarea,
  type ResourceField,
  type SafeParseValidator,
} from "@novemai/components";
import {
  isStrictIsoCalendarDate,
  partialDayMinutes,
  HOURS_PER_WORKING_DAY,
} from "@novemai/time-attendance-sdk";

import type { LeavePreview } from "@/features/leave/leave-api";
import { LeaveUsageNotes, previewBlocksSubmit } from "@/features/leave/leave-usage-notes";

/** The validated leave-request draft this form produces. */
export interface LeaveRequestDraft {
  readonly policy_id: string;
  readonly start_date: string;
  readonly end_date: string;
  readonly partial_days?: readonly {
    readonly date: string;
    readonly start_time: string;
    readonly end_time: string;
  }[];
  readonly reason: string;
  /** ONE native delegation target (gap §9.1) — empty string = no delegate. */
  readonly delegate_employee_id: string;
  /** Unpaid travel-leave days on top of an annual request (LV-10); absent = none. */
  readonly travel_leave_days?: number;
}

/**
 * Hand-rolled validator satisfying the @novemai/components SafeParseValidator
 * port — the shared library is zod-compatible WITHOUT the app taking a zod
 * dependency. Returns localized message KEYS the host has already resolved via
 * the messages map passed in (so error text stays i18n, ADR-006).
 */
function makeLeaveValidator(
  msg: (key: string) => string,
  travelMaxFor: (policyId: string) => number,
): SafeParseValidator<LeaveRequestDraft> {
  return {
    safeParse(input: unknown) {
      const raw = (input ?? {}) as Record<string, unknown>;
      const issues: Array<{ path: string[]; message: string }> = [];

      const policyId = String(raw["policy_id"] ?? "").trim();
      if (policyId.length === 0) {
        issues.push({
          path: ["policy_id"],
          message: msg("leave.form.error.policy_required"),
        });
      }

      const startDate = String(raw["start_date"] ?? "");
      const startDateValid = isStrictIsoCalendarDate(startDate);
      if (!startDateValid) {
        issues.push({
          path: ["start_date"],
          message: msg("leave.form.error.start_invalid"),
        });
      }
      const endDate = String(raw["end_date"] ?? "");
      const endDateValid = isStrictIsoCalendarDate(endDate);
      if (!endDateValid) {
        issues.push({
          path: ["end_date"],
          message: msg("leave.form.error.end_invalid"),
        });
      }
      if (startDateValid && endDateValid && endDate < startDate) {
        issues.push({
          path: ["end_date"],
          message: msg("leave.form.error.end_before_start"),
        });
      }

      const hourly = raw["duration_mode"] === "hourly";
      const startTime = String(raw["start_time"] ?? "");
      const endTime = String(raw["end_time"] ?? "");
      if (hourly) {
        if (startDate !== endDate)
          issues.push({
            path: ["end_date"],
            message: msg("leave.form.error.same_day"),
          });
        const minutes = partialDayMinutes({
          date: startDate,
          startTime,
          endTime,
        });
        if (minutes === null || minutes > HOURS_PER_WORKING_DAY * 60)
          issues.push({
            path: ["end_time"],
            message: msg("leave.form.error.time_window"),
          });
      }

      // The field only exists for a type that allows travel leave; for any other the
      // raw value is ignored, so a stale entry from a previously chosen type is not sent.
      const travelMax = travelMaxFor(policyId);
      let travelLeaveDays = 0;
      const rawTravel = String(raw["travel_leave_days"] ?? "").trim();
      if (travelMax > 0 && rawTravel !== "") {
        const parsed = Number(rawTravel);
        if (!Number.isInteger(parsed) || parsed < 0 || parsed > travelMax) {
          issues.push({
            path: ["travel_leave_days"],
            message: msg("leave.form.error.travel_days_invalid"),
          });
        } else {
          travelLeaveDays = parsed;
        }
      }

      if (issues.length > 0) {
        return { success: false, error: { issues } };
      }
      return {
        success: true,
        data: {
          policy_id: policyId,
          start_date: startDate,
          end_date: endDate,
          ...(hourly && {
            partial_days: [
              { date: startDate, start_time: startTime, end_time: endTime },
            ],
          }),
          reason: String(raw["reason"] ?? "").trim(),
          delegate_employee_id: String(
            raw["delegate_employee_id"] ?? "",
          ).trim(),
          ...(travelLeaveDays > 0 && { travel_leave_days: travelLeaveDays }),
        },
      };
    },
  };
}

export interface LeaveFormContentProps {
  /**
   * Submit handler — the host wires this to the POST /v1/leave/requests
   * mutation (added in B1; B0 establishes the form + content convention). The
   * draft carries dates and optional local-clock windows; the server derives hours.
   */
  readonly onSubmit: (draft: LeaveRequestDraft) => void | Promise<void>;
  readonly onPreview: (draft: LeaveRequestDraft) => Promise<LeavePreview>;
  /**
   * The policies the requester may draw from — the host resolves these from
   * the caller's OWN balance rows, so the set is exactly what they can spend.
   * `travelLeaveMaxDays` > 0 makes the unpaid travel-leave field appear for that
   * policy (LV-10); absent or 0 = the tenant has not enabled it.
   */
  readonly policyOptions?: readonly {
    readonly value: string;
    readonly label: string;
    readonly travelLeaveMaxDays?: number;
  }[];
  /**
   * Native delegation candidates (gap §9.1) — own-org employees the requester
   * may delegate to while away. ONE picker, NOT a dual-ERP selector.
   */
  readonly delegateOptions?: readonly {
    readonly value: string;
    readonly label: string;
  }[];
}

export function LeaveFormContent({
  onSubmit,
  onPreview,
  policyOptions = [],
  delegateOptions = [],
}: LeaveFormContentProps): React.ReactElement {
  const { t, i18n } = useTranslation();
  const [hourly, setHourly] = useState(false);
  const [preview, setPreview] = useState<(LeavePreview & { key: string }) | null>(null);
  const [previewError, setPreviewError] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState("");
  const revision = useRef(0);

  const travelMaxFor = (policyId: string): number =>
    policyOptions.find((option) => option.value === policyId)?.travelLeaveMaxDays ?? 0;
  const travelMax = travelMaxFor(selectedPolicy);

  async function previewOrSubmit(draft: LeaveRequestDraft): Promise<void> {
    const key = JSON.stringify(draft);
    if (preview?.key === key) return previewBlocksSubmit(preview) ? undefined : onSubmit(draft);
    const current = revision.current;
    setPreviewError(false);
    try {
      const result = await onPreview(draft);
      if (revision.current === current) setPreview({ key, ...result });
    } catch {
      if (revision.current === current) setPreviewError(true);
    }
  }

  const fields: readonly (Omit<ResourceField, "type"> & {
    type?: ResourceField["type"] | "time";
    hint?: string;
  })[] = [
    {
      // A picker, not a text box: policy_id is a uuid the requester has no way
      // to know — `error.policy_required` has always said "choose", and the
      // only way to obey a free-text field was to retype the id shown on the
      // balance side card. The leading empty option keeps the required check
      // meaningful (a pre-selected first policy would submit silently).
      name: "policy_id",
      label: t("leave.form.policy"),
      type: "select",
      required: true,
      options: [
        { value: "", label: t("leave.form.policy_placeholder") },
        ...policyOptions,
      ],
    },
    {
      name: "start_date",
      label: t("leave.form.start_date"),
      type: "date",
      required: true,
    },
    {
      name: "end_date",
      label: t("leave.form.end_date"),
      type: "date",
      required: true,
    },
    {
      name: "duration_mode",
      label: t("leave.form.duration_mode"),
      type: "select",
      defaultValue: "full",
      options: [
        { value: "full", label: t("leave.form.full_days") },
        { value: "hourly", label: t("leave.form.hourly") },
      ],
    },
    ...(hourly
      ? [
          {
            name: "start_time",
            label: t("leave.form.start_time"),
            type: "time" as const,
            required: true,
          },
          {
            name: "end_time",
            label: t("leave.form.end_time"),
            type: "time" as const,
            required: true,
          },
        ]
      : []),
    ...(travelMax > 0
      ? [
          {
            // Unpaid travel leave on top of annual leave (LV-10). Only offered for a
            // type the tenant enabled it on; the server re-checks the limit.
            name: "travel_leave_days",
            label: t("leave.form.travel_days"),
            type: "number" as const,
            hint: t("leave.form.travel_days_hint", { max: travelMax }),
          },
        ]
      : []),
    { name: "reason", label: t("leave.form.reason"), type: "textarea" },
    {
      // ONE native delegation field (gap §9.1) — replaces the İSTON dual-ERP
      // (Axapta + EBS) vekâlet selector with a single own-org picker.
      name: "delegate_employee_id",
      label: t("leave.form.delegate"),
      type: "select",
      options: [
        { value: "", label: t("leave.form.delegate_none") },
        ...delegateOptions,
      ],
    },
  ];

  // novem form language (Faz 8.6 / A1 — emp-request-forms.jsx FormScaffold
  // port): serif page head + the form inside a Card surface. Layout only;
  // shared form validation remains; preview is a read-only first step.
  return (
    <div className="space-y-6">
      <EntityForm<LeaveRequestDraft>
        className="space-y-6"
        validator={makeLeaveValidator(t, travelMaxFor)}
        onValidSubmit={previewOrSubmit}
        onChange={(event) => {
          revision.current += 1;
          setPreview(null);
          setPreviewError(false);
          const data = new FormData(event.currentTarget);
          setHourly(data.get("duration_mode") === "hourly");
          setSelectedPolicy(String(data.get("policy_id") ?? ""));
        }}
      >
        {({ errors, submitting }) => (
          <>
            <PageHeader
              title={t("leave.form.title")}
              description={t("leave.form.description")}
              actions={
                <div data-slot="resource-form-actions">
                  {/* The server re-checks and refuses with 409 leave.split_rule_violated;
                      disabling here only saves the round trip. Editing any field clears
                      the preview, so the requester can always reshape the request. */}
                  <Button
                    type="submit"
                    disabled={submitting || previewBlocksSubmit(preview)}
                    aria-busy={submitting || undefined}
                  >
                    {preview === null
                      ? t("leave.form.calculate")
                      : t("leave.form.submit")}
                  </Button>
                </div>
              }
            />
            <Card>
              <CardBody className="flex flex-col gap-3.5">
                {fields.map((field) => (
                  <FormField
                    key={field.name}
                    label={field.label}
                    error={errors[field.name]}
                    required={field.required ?? false}
                    {...(field.hint !== undefined && { hint: field.hint })}
                  >
                    {field.type === "select" ? (
                      <Select
                        name={field.name}
                        defaultValue={field.defaultValue ?? ""}
                        required={field.required ?? false}
                      >
                        {field.options?.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                    ) : field.type === "textarea" ? (
                      <Textarea name={field.name} />
                    ) : field.type === "number" ? (
                      <Input
                        name={field.name}
                        type="number"
                        min={0}
                        max={travelMax}
                        step={1}
                        defaultValue={0}
                      />
                    ) : (
                      <Input
                        name={field.name}
                        type={field.type === "time" ? "time" : "date"}
                        required={field.required}
                      />
                    )}
                  </FormField>
                ))}
                {hourly && (
                  <p className="nai-form-field-hint">
                    {t("leave.form.hourly_hint")}
                  </p>
                )}
                {preview !== null && (
                  <output
                    aria-live="polite"
                    className="block text-[var(--fg-primary)]"
                  >
                    {t("leave.form.duration_preview", {
                      days: new Intl.NumberFormat(i18n.language).format(
                        preview.working_days,
                      ),
                      hours: new Intl.NumberFormat(i18n.language, {
                        maximumFractionDigits: 2,
                      }).format(preview.hours),
                    })}
                  </output>
                )}
                {preview !== null && <LeaveUsageNotes preview={preview} />}
                {previewError && (
                  <p role="alert" className="nai-form-field-error">
                    {t("leave.form.preview_error")}
                  </p>
                )}
              </CardBody>
            </Card>
          </>
        )}
      </EntityForm>
    </div>
  );
}
